import React from "react";

interface Props {
  /** Panel title for the error message. */
  title?: string;
  children: React.ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Isolates a single panel: a render/runtime error in one panel shows an inline
 * message instead of blanking the whole dashboard. Production-safety net.
 */
export default class PanelErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error): void {
    console.error("Panel render error:", this.props.title ?? "", error);
  }

  render(): React.ReactNode {
    if (this.state.error) {
      return (
        <div className="w-full h-full flex items-center justify-center p-3 text-center">
          <div>
            <div className="text-xl mb-1">⚠️</div>
            <div className="text-xs font-medium text-gray-600 dark:text-gray-300">
              {this.props.title ?? "Panel"} failed to render
            </div>
            <div className="text-[11px] text-gray-400 mt-0.5 max-w-[220px] truncate">
              {this.state.error.message}
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
